import { useEffect, useState } from "react";
import { api, money } from "@/hub/lib/api";
import { Link } from "react-router-dom";
import {
  UserPlus, Trash2, Copy, ShieldAlert, MapPin, Utensils, Users as UsersIcon, Pencil, Plus, Package, Mountain, ArrowUpRight, Layers, Soup,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import { catColor } from "@/hub/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function useRole() {
  const [role, setRole] = useState(() => localStorage.getItem("lush_role") || "RM");
  useEffect(() => {
    const h = () => setRole(localStorage.getItem("lush_role") || "RM");
    window.addEventListener("role-change", h);
    return () => window.removeEventListener("role-change", h);
  }, []);
  return role;
}

export default function Settings() {
  const role = useRole();
  if (role !== "Admin") {
    return (
      <div className="bg-white border border-dashed border-stone-300 p-12 text-center max-w-lg mx-auto mt-10" data-testid="settings-locked">
        <ShieldAlert className="h-10 w-10 mx-auto text-terracotta mb-3" />
        <h3 className="font-heading text-lg font-medium">Admin access required</h3>
        <p className="text-sm text-stone-500 mt-1">Switch to <span className="font-semibold text-terracotta">Admin</span> mode using the toggle in the sidebar to manage packages, activities, accommodation, meals and RM codes.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Link to="/hub/packages" className="bg-white border border-border p-4 flex items-center justify-between hover:border-forest transition-colors group" data-testid="settings-packages-link">
          <span className="flex items-center gap-2 text-sm font-medium"><Package className="h-4 w-4 text-forest" /> Manage Packages</span>
          <ArrowUpRight className="h-4 w-4 text-stone-300 group-hover:text-forest" />
        </Link>
        <Link to="/hub/activities" className="bg-white border border-border p-4 flex items-center justify-between hover:border-forest transition-colors group" data-testid="settings-activities-link">
          <span className="flex items-center gap-2 text-sm font-medium"><Mountain className="h-4 w-4 text-forest" /> Manage Activities</span>
          <ArrowUpRight className="h-4 w-4 text-stone-300 group-hover:text-forest" />
        </Link>
      </div>

      <Tabs defaultValue="team">
        <TabsList className="rounded-sm">
          <TabsTrigger value="team" data-testid="tab-team"><UsersIcon className="h-4 w-4 mr-1.5" /> Team & RM Codes</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Layers className="h-4 w-4 mr-1.5" /> Activity Categories</TabsTrigger>
          <TabsTrigger value="zones" data-testid="tab-zones"><MapPin className="h-4 w-4 mr-1.5" /> Zones & Accommodation</TabsTrigger>
          <TabsTrigger value="meals" data-testid="tab-meals"><Utensils className="h-4 w-4 mr-1.5" /> Meal Breakdown</TabsTrigger>
          <TabsTrigger value="menus" data-testid="tab-menus"><Soup className="h-4 w-4 mr-1.5" /> Menus</TabsTrigger>
        </TabsList>
        <TabsContent value="team" className="mt-4"><TeamTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
        <TabsContent value="zones" className="mt-4"><ZonesTab /></TabsContent>
        <TabsContent value="meals" className="mt-4"><MealsTab /></TabsContent>
        <TabsContent value="menus" className="mt-4"><MenusTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function TeamTab() {
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.users().then(setUsers);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Enter RM name");
    const u = await api.createUser({ name: name.trim() });
    toast.success(`Created ${u.name} · code ${u.code}`);
    setName("");
    load();
    window.dispatchEvent(new Event("users-change"));
  };
  const toggle = async (u) => { await api.updateUser(u.id, { active: !u.active }); load(); window.dispatchEvent(new Event("users-change")); };
  const del = async (u) => { if (window.confirm(`Delete ${u.name}?`)) { await api.deleteUser(u.id); toast.success("Deleted"); load(); window.dispatchEvent(new Event("users-change")); } };
  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); };

  return (
    <div className="bg-white border border-border">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="RM full name" className="rounded-sm max-w-xs" data-testid="rm-name-input" />
        <Button onClick={create} size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="create-rm-btn"><UserPlus className="h-4 w-4 mr-1" /> Create RM Code</Button>
      </div>
      {users.length === 0 ? (
        <div className="p-10 text-center text-sm text-stone-400">No users yet. Create RM codes so they can log their business.</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-border">
            <th className="px-5 py-3 font-semibold">Name</th><th className="px-5 py-3 font-semibold">Code</th><th className="px-5 py-3 font-semibold">Role</th><th className="px-5 py-3 font-semibold">Active</th><th className="px-5 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3"><button onClick={() => copy(u.code)} className="font-mono text-forest inline-flex items-center gap-1.5 hover:text-terracotta" data-testid={`copy-code-${u.id}`}>{u.code} <Copy className="h-3 w-3" /></button></td>
                <td className="px-5 py-3 text-stone-500">{u.role}</td>
                <td className="px-5 py-3"><Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`toggle-user-${u.id}`} /></td>
                <td className="px-5 py-3 text-right"><Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" onClick={() => del(u)} data-testid={`del-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [name, setName] = useState("");
  const load = () => { api.categories().then(setCategories); api.activities().then(setActivities); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return toast.error("Enter category name");
    await api.createCategory({ name: name.trim(), order: categories.length + 1 });
    toast.success("Category added"); setName(""); load();
  };
  const del = async (c) => {
    const count = activities.filter((a) => a.category === c.name).length;
    if (window.confirm(`Delete "${c.name}"?${count ? ` ${count} item(s) will keep this label until reassigned.` : ""}`)) {
      await api.deleteCategory(c.id); toast.success("Deleted"); load();
    }
  };
  return (
    <div className="bg-white border border-border">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="New category name" className="rounded-sm max-w-xs" data-testid="category-name-input" />
        <Button onClick={create} size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="create-category-btn"><Plus className="h-4 w-4 mr-1" /> Add Category</Button>
      </div>
      <div className="divide-y divide-border">
        {categories.map((c) => (
          <div key={c.id} className="px-5 py-3 flex items-center justify-between" data-testid={`category-row-${c.id}`}>
            <span className="text-sm font-medium flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm" style={{ background: catColor(c.name) }} /> {c.name}
              <span className="text-xs text-stone-400">{activities.filter((a) => a.category === c.name).length} items</span>
            </span>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" onClick={() => del(c)} data-testid={`del-category-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ZonesTab() {
  const [zones, setZones] = useState([]);
  const load = () => api.zones().then(setZones);
  useEffect(() => { load(); }, []);
  const del = async (z) => { if (window.confirm(`Delete ${z.name}?`)) { await api.deleteZone(z.id); toast.success("Deleted"); load(); } };

  return (
    <div className="bg-white border border-border">
      <div className="p-4 border-b border-border flex justify-between items-center">
        <span className="text-sm text-stone-500">Outdoor zones and accommodation types RMs can reserve.</span>
        <ZoneDialog onDone={load} />
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-border">
          <th className="px-5 py-3 font-semibold">Zone</th><th className="px-5 py-3 font-semibold">Type</th><th className="px-5 py-3 font-semibold text-right">Capacity</th><th className="px-5 py-3 font-semibold">Color</th><th className="px-5 py-3 font-semibold text-right">Actions</th>
        </tr></thead>
        <tbody>
          {zones.map((z) => (
            <tr key={z.id} className="border-b border-border last:border-0" data-testid={`settings-zone-${z.id}`}>
              <td className="px-5 py-3 font-medium">{z.name}</td>
              <td className="px-5 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${z.type === "accommodation" ? "bg-terracotta text-white" : "bg-forest text-white"}`}>{z.type}</span></td>
              <td className="px-5 py-3 text-right font-mono">{z.capacity}</td>
              <td className="px-5 py-3"><span className="inline-flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded-sm" style={{ background: z.color }} /> <span className="font-mono text-xs text-stone-400">{z.color}</span></span></td>
              <td className="px-5 py-3 text-right">
                <div className="flex justify-end gap-1">
                  <ZoneDialog existing={z} onDone={load} trigger={<Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`edit-zone-${z.id}`}><Pencil className="h-3.5 w-3.5" /></Button>} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" onClick={() => del(z)} data-testid={`del-zone-${z.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ZoneDialog({ existing, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "outdoor", capacity: "", color: "#6B7280", description: "" });
  useEffect(() => {
    if (open) setForm(existing ? { name: existing.name, type: existing.type, capacity: existing.capacity, color: existing.color, description: existing.description || "" } : { name: "", type: "outdoor", capacity: "", color: "#6B7280", description: "" });
  }, [open, existing]);
  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    const payload = { ...form, capacity: Number(form.capacity) || 0 };
    if (existing) await api.updateZone(existing.id, payload); else await api.createZone(payload);
    toast.success(existing ? "Zone updated" : "Zone added");
    setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="new-zone-btn"><Plus className="h-4 w-4 mr-1" /> New Zone</Button>}</DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle className="font-heading">{existing ? "Edit Zone" : "New Zone / Accommodation"}</DialogTitle><DialogDescription>Name, type, capacity and calendar color.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="zf-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="zf-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="outdoor">Outdoor</SelectItem><SelectItem value="accommodation">Accommodation</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="rounded-sm font-mono" data-testid="zf-capacity" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Color (hex)</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-12 rounded-sm border border-border" data-testid="zf-color" />
              <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-sm font-mono" />
            </div>
          </div>
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="zf-desc" /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="save-zone-btn">{existing ? "Save" : "Add Zone"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MenuEditSheet({ menu, meals, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", days: 1, description: "" });
  const [components, setComponents] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({ name: menu.name, price: menu.price || "", days: menu.days || 1, description: menu.description || "" });
      setComponents((menu.components || []).map((c) => ({ day: c.day, meal: c.meal, detail: c.detail || "" })));
    }
  }, [open, menu]);

  const addComp = () => setComponents((c) => [...c, { day: 1, meal: meals[0]?.name || "", detail: "" }]);
  const setComp = (i, k, v) => setComponents((c) => c.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmComp = (i) => setComponents((c) => c.filter((_, idx) => idx !== i));
  const dayOptions = Array.from({ length: Number(form.days) || 1 }, (_, i) => i + 1);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Enter menu name");
    if (components.length === 0) return toast.error("Add at least one meal component");
    await api.updateMenu(menu.id, {
      name: form.name.trim(), price: Number(form.price) || 0, days: Number(form.days) || 1,
      components: components.map((c) => ({ day: Number(c.day) || 1, meal: c.meal, detail: c.detail })),
      description: form.description,
    });
    toast.success("Menu updated");
    setOpen(false); onDone();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`edit-menu-${menu.id}`}><Pencil className="h-3.5 w-3.5" /></Button></SheetTrigger>
      <SheetContent className="overflow-y-auto lush-scroll w-full sm:max-w-lg">
        <SheetHeader><SheetTitle className="font-heading">Edit Menu</SheetTitle><SheetDescription>Update the menu name, price and day-basis meal components.</SheetDescription></SheetHeader>
        <div className="space-y-4 mt-5">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-stone-400">Menu Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="menu-edit-name" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-sm font-mono" data-testid="menu-edit-price" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Days</Label><Input type="number" min="1" value={form.days} onChange={(e) => { const d = e.target.value; setForm({ ...form, days: d }); setComponents((cs) => cs.map((c) => ({ ...c, day: Math.min(Number(c.day) || 1, Number(d) || 1) }))); }} className="rounded-sm font-mono" data-testid="menu-edit-days" /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-400">Meal Components (day basis)</Label>
            <div className="space-y-2 mt-1">
              {components.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`menu-edit-comp-${i}`}>
                  <Select value={String(c.day)} onValueChange={(v) => setComp(i, "day", v)}>
                    <SelectTrigger className="rounded-sm col-span-2 h-9" data-testid={`menu-edit-comp-day-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{dayOptions.map((d) => <SelectItem key={d} value={String(d)}>Day {d}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={c.meal} onValueChange={(v) => setComp(i, "meal", v)}>
                    <SelectTrigger className="rounded-sm col-span-4 h-9" data-testid={`menu-edit-comp-meal-${i}`}><SelectValue placeholder="Meal" /></SelectTrigger>
                    <SelectContent>{meals.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={c.detail} onChange={(e) => setComp(i, "detail", e.target.value)} placeholder="Detail" className="rounded-sm col-span-5 h-9" data-testid={`menu-edit-comp-detail-${i}`} />
                  <button onClick={() => rmComp(i)} className="col-span-1 flex justify-center" data-testid={`menu-edit-comp-rm-${i}`}><Trash2 className="h-4 w-4 text-stone-300 hover:text-red-500" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-sm w-full border-dashed" onClick={addComp} data-testid="menu-edit-add-comp"><Plus className="h-3.5 w-3.5 mr-1" /> Add meal component</Button>
            </div>
          </div>
        </div>
        <SheetFooter className="mt-6"><Button onClick={save} className="rounded-sm bg-forest hover:bg-forest-hover w-full" data-testid="save-menu-edit">Save Menu</Button></SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function MenusTab() {
  const [menus, setMenus] = useState([]);
  const [meals, setMeals] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", days: 1, description: "" });
  const [components, setComponents] = useState([]);
  const load = () => { api.menus().then(setMenus); api.meals().then(setMeals); };
  useEffect(() => { load(); }, []);

  const addComp = () => setComponents((c) => [...c, { day: 1, meal: meals[0]?.name || "", detail: "" }]);
  const setComp = (i, k, v) => setComponents((c) => c.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmComp = (i) => setComponents((c) => c.filter((_, idx) => idx !== i));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Enter menu name");
    if (components.length === 0) return toast.error("Add at least one meal component");
    await api.createMenu({
      name: form.name.trim(), price: Number(form.price) || 0, days: Number(form.days) || 1,
      components: components.map((c) => ({ day: Number(c.day) || 1, meal: c.meal, detail: c.detail })),
      description: form.description,
    });
    toast.success("Menu created");
    setForm({ name: "", price: "", days: 1, description: "" }); setComponents([]); load();
  };
  const del = async (m) => { if (window.confirm(`Delete ${m.name}?`)) { await api.deleteMenu(m.id); toast.success("Deleted"); load(); } };

  const dayOptions = Array.from({ length: Number(form.days) || 1 }, (_, i) => i + 1);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-border p-5">
        <h3 className="font-heading text-base font-medium mb-3 flex items-center gap-2"><Soup className="h-4 w-4 text-forest" /> Create Standard Menu</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider text-stone-400">Menu Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="menu-name-input" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Price (LKR)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-sm font-mono" data-testid="menu-price-input" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Days</Label><Input type="number" min="1" value={form.days} onChange={(e) => { const d = e.target.value; setForm({ ...form, days: d }); setComponents((cs) => cs.map((c) => ({ ...c, day: Math.min(Number(c.day) || 1, Number(d) || 1) }))); }} className="rounded-sm font-mono" data-testid="menu-days-input" /></div>
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-stone-400">Meal Components (day basis)</Label>
          <div className="space-y-2 mt-1">
            {components.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`menu-comp-${i}`}>
                <Select value={String(c.day)} onValueChange={(v) => setComp(i, "day", v)}>
                  <SelectTrigger className="rounded-sm col-span-2 h-9" data-testid={`menu-comp-day-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{dayOptions.map((d) => <SelectItem key={d} value={String(d)}>Day {d}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={c.meal} onValueChange={(v) => setComp(i, "meal", v)}>
                  <SelectTrigger className="rounded-sm col-span-4 h-9" data-testid={`menu-comp-meal-${i}`}><SelectValue placeholder="Meal" /></SelectTrigger>
                  <SelectContent>{meals.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={c.detail} onChange={(e) => setComp(i, "detail", e.target.value)} placeholder="Detail e.g. 2 items, buffet" className="rounded-sm col-span-5 h-9" data-testid={`menu-comp-detail-${i}`} />
                <button onClick={() => rmComp(i)} className="col-span-1 flex justify-center" data-testid={`menu-comp-rm-${i}`}><Trash2 className="h-4 w-4 text-stone-300 hover:text-red-500" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-sm w-full border-dashed" onClick={addComp} data-testid="menu-add-comp"><Plus className="h-3.5 w-3.5 mr-1" /> Add meal component</Button>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={create} size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="create-menu-btn">Save Menu</Button>
        </div>
      </div>

      <div className="bg-white border border-border">
        <div className="px-5 py-3 border-b border-border text-xs uppercase tracking-wider text-stone-400 font-semibold">Standard Menus</div>
        {menus.length === 0 ? <div className="p-6 text-sm text-stone-400 text-center">No menus yet.</div> : (
          <div className="divide-y divide-border">
            {menus.map((m) => (
              <div key={m.id} className="p-5" data-testid={`menu-row-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-heading font-medium">{m.name}</span>
                    <span className="text-xs text-stone-400 ml-2">{m.days} day(s) · {m.components.length} items</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-forest">{money(m.price)}</span>
                    <MenuEditSheet menu={m} meals={meals} onDone={load} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" onClick={() => del(m)} data-testid={`del-menu-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.components.map((c, i) => (
                    <span key={i} className="text-[11px] bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-sm">D{c.day} · {c.meal}{c.detail ? ` (${c.detail})` : ""}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MealEditDialog({ meal, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(meal.name);
  const [price, setPrice] = useState(meal.price || "");
  useEffect(() => { if (open) { setName(meal.name); setPrice(meal.price || ""); } }, [open, meal]);
  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    await api.updateMeal(meal.id, { name: name.trim(), price: Number(price) || 0, category: meal.category || "Meal" });
    toast.success("Meal updated"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`edit-meal-${meal.id}`}><Pencil className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle className="font-heading">Edit Meal</DialogTitle><DialogDescription>Update the meal name and price.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="meal-edit-name" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Price (LKR)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-sm font-mono" data-testid="meal-edit-price" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="save-meal-edit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MealsTab() {
  const [meals, setMeals] = useState([]);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const load = () => api.meals().then(setMeals);
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return toast.error("Enter meal name");
    await api.createMeal({ name: name.trim(), price: Number(price) || 0 });
    toast.success("Meal added"); setName(""); setPrice(""); load();
  };
  const del = async (m) => { if (window.confirm(`Delete ${m.name}?`)) { await api.deleteMeal(m.id); toast.success("Deleted"); load(); } };

  return (
    <div className="bg-white border border-border">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Meal / refreshment name" className="rounded-sm max-w-xs" data-testid="meal-name-input" />
        <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price (LKR, optional)" className="rounded-sm w-40 font-mono" data-testid="meal-price-input" />
        <Button onClick={create} size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="create-meal-btn"><Plus className="h-4 w-4 mr-1" /> Add Meal</Button>
      </div>
      <div className="divide-y divide-border">
        {meals.map((m) => (
          <div key={m.id} className="px-5 py-3 flex items-center justify-between" data-testid={`meal-row-${m.id}`}>
            <span className="text-sm font-medium flex items-center gap-2"><Utensils className="h-3.5 w-3.5 text-stone-400" /> {m.name}</span>
            <div className="flex items-center gap-3">
              {m.price > 0 && <span className="font-mono text-xs text-stone-500">{money(m.price)}</span>}
              <MealEditDialog meal={m} onDone={load} />
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" onClick={() => del(m)} data-testid={`del-meal-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
